import {ActionIcon, Button, Menu, createStyles} from '@mantine/core';
import React, {useCallback, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import createChatForEntry from 'llm/data-capture/createChatForEntry';
import AsyncButton, {AsyncButtonLoadingBehavior} from 'ui-library/buttons/AsyncButton';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface StartChatWithEntryButtonProps {
  entryId: string;
  showEditContents: boolean;
  isEditable: boolean;
  showAnalyzeData: boolean;
}

const useStyles = createStyles((theme) => ({
  mainButton: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderRight: 'none',
  },
  chevronButton: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: `1px solid ${theme.colors.blue[3]}`,
  },
}));

function getComposeLabel(isEditable: boolean): string {
  return isEditable ? 'Edit contents' : 'Ask questions';
}

function getComposeAgentType(isEditable: boolean): AgentType {
  if (BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT) {
    return AgentType.ASSISTANT;
  }
  const canAsk = BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH;
  return isEditable || !canAsk ? AgentType.DATA_CAPTURE : AgentType.ASK;
}

export default function StartChatWithEntryButton({
  entryId,
  showEditContents,
  isEditable,
  showAnalyzeData,
}: StartChatWithEntryButtonProps): JSX.Element {
  const {classes} = useStyles();
  const [isLoading, setIsLoading] = useState(false);

  const handleCompose = useCallback(async () => {
    setIsLoading(true);
    try {
      await createChatForEntry(entryId, getComposeAgentType(isEditable));
    } finally {
      setIsLoading(false);
    }
  }, [entryId, isEditable]);

  const handleAnalysis = useCallback(
    () =>
      BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT
        ? createChatForEntry(entryId, AgentType.ASSISTANT, {assistantScope: 'read'})
        : createChatForEntry(entryId, AgentType.ANALYSIS),
    [entryId]
  );

  // Single option: compose only
  if (showEditContents && !showAnalyzeData) {
    return (
      <AsyncButton
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        icon="sparkle"
        loadingBehavior={AsyncButtonLoadingBehavior.SPINNER_WITH_TEXT}
        onClick={handleCompose}
        size={ButtonSize.MINI}
        text={getComposeLabel(isEditable)}
      />
    );
  }

  // Single option: analysis only
  if (showAnalyzeData && !showEditContents) {
    return (
      <AsyncButton
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        icon="lightbulb"
        loadingBehavior={AsyncButtonLoadingBehavior.SPINNER_WITH_TEXT}
        onClick={handleAnalysis}
        size={ButtonSize.MINI}
        text="Analyze data"
      />
    );
  }

  // Both options, editable: split button
  if (isEditable) {
    return (
      <Button.Group>
        <Button
          className={classes.mainButton}
          color="blue"
          leftIcon={<BenchlingIcon icon="sparkle" />}
          loading={isLoading}
          onClick={handleCompose}
          size="xs"
          variant="secondary"
        >
          Edit contents
        </Button>
        <Menu shadow="md" position="bottom-end">
          <Menu.Target>
            <ActionIcon
              aria-label="More actions"
              className={classes.chevronButton}
              color="blue"
              disabled={isLoading}
              size="xs"
              variant="secondary"
            >
              <BenchlingIcon icon="chevron-down" />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item onClick={handleAnalysis}>Analyze data</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Button.Group>
    );
  }

  // Both options, not editable: behavior depends on assistant agent availability
  if (BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT) {
    return (
      <AsyncButton
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        icon="lightbulb"
        loadingBehavior={AsyncButtonLoadingBehavior.SPINNER_WITH_TEXT}
        onClick={handleAnalysis}
        size={ButtonSize.MINI}
        text="Analyze data"
      />
    );
  }

  return (
    <AsyncButton
      buttonStyle={ButtonStyle.TERTIARY_BLUE}
      icon="sparkle"
      loadingBehavior={AsyncButtonLoadingBehavior.SPINNER_WITH_TEXT}
      onClick={handleCompose}
      size={ButtonSize.MINI}
      text="Ask questions"
    />
  );
}
